/* eslint-disable */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'formik';
import {
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FormikTextField from 'components/FormikFields/FormikTextField';
import USER_ROLES from 'components/Users/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ContentWrapper from '../../../Wrappers/ContentWrapper';
import Loader from '../../../Shared/Loader';
import { REQUEST_STATUS } from '../../../../redux_store/constants';
import AllowedAccountsSection from './AllowedAccountsSection/AllowedAccountsSection';
import AllowedAccountsSectionModal from './AllowedAccountsSection/widgets/AllowedAccountsModal';
import RoleSelector from './RoleSelector';
import UserPermissionSection from './UserPermissionSection';
import useMetadataUpdate from '../../../../hooks/useMetadataUpdate';
import { useQuery } from 'react-fetching-library';

const useStyles = makeStyles((theme) => ({
  section: {
    width: '100%',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#031F42',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors?.lightBlue || '#00A8FF'}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1),
    },
  },
  sectionContent: {
    marginTop: theme.spacing(2),
    maxWidth: 1000,
    width: '100%',
  },
  providerFieldsContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    borderRadius: 8,
    border: '1px solid rgba(0, 168, 255, 0.1)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: 'linear-gradient(90deg, #00A8FF 0%, #6C7FDF 100%)',
      borderRadius: '8px 8px 0 0',
    },
  },
  providerFieldsTitle: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '"✓"',
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  accountAccessHeader: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: '2px solid #E0E0E0',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -theme.spacing(2),
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: '#E0E0E0',
    },
  },
}));

const UserForm = ({ values, backendErrors, setBackendErrors, initAllowedAccounts }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const isProvider = values.role === USER_ROLES.PROVIDER;
  const { accounts, status } = useSelector((state) => state.accounts);
  const [openAllowedAccountsModal, toggleAllowedAccountsModal] = useState(false);
  const [isOwnerAccDS, setIsOwnerAccDS] = useState(false);

  const {
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const { accountUuid: paramAccountUuid } = useParams();

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${paramAccountUuid}`,
    },
    false,
  ]);

  const checkIsOwnerAccDS = async () => {
    // "ReceiveSurveysEmails"  and "ReceiveClaimsEmails" should only be shown to Non DS account users regardless of what is the current account
    // "AllowedAccountsSection" should only show If I'm editing a DS account user

    // Non DS account user means the user I'm editing right now belong to a Non DS account
    // DS account user means the user I'm editing right now belong to a DS account
    const isItChildAccount = !!paramAccountUuid;
    if (isItChildAccount) {
      const result = await query();
      setIsOwnerAccDS(result?.payload?.accountType === 'distributionSponsor');
    } else {
      setIsOwnerAccDS(isDistributionSponsor);
    }
  }

  useEffect(() => {
    checkIsOwnerAccDS()
  }, [accounts]);

  return (
    <>
      {status !== REQUEST_STATUS.loading && isOwnerAccDS !== null ? (
        <Form>
          <Field name="role" type="text">
            {({ form }) => (
              <>
                {
                  openAllowedAccountsModal ? <AllowedAccountsSectionModal
                      values={values}
                      initAllowedAccounts={initAllowedAccounts}
                      changeAllowedAccounts={(val) => {
                        form.setFieldValue('allowedAccounts', val);
                      }}
                      openAllowedAccountsModal={openAllowedAccountsModal}
                      toggleAllowedAccountsModal={toggleAllowedAccountsModal}
                    />
                    : <div />
                }
              </>
            )}
          </Field>
          <Grid container direction="column" alignItems="flex-start">
            {/* User Details Section */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {forms.userDetails}
              </Typography>
              <div className={classes.sectionContent}>
                <FormikTextField
                  type="text"
                  name="firstName"
                  label={forms.firstName}
                  backendError={backendErrors.firstName}
                  required
                  disabled={values.doesUserExist}
                />
                <FormikTextField
                  type="text"
                  name="lastName"
                  label={forms.lastName}
                  backendError={backendErrors.lastName}
                  required
                  disabled={values.doesUserExist}
                />
                <FormikTextField
                  type="text"
                  name="email"
                  label={forms.email}
                  required
                  backendError={backendErrors.email}
                />
              </div>
            </div>

            {/* User Role Section */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                {forms.userRoleDetails}
              </Typography>
              <div className={classes.sectionContent}>
                <Field name="role" type="text">
                  {({ field, form }) => (
                    <RoleSelector
                      value={field.value}
                      onChange={(value) => form.setFieldValue('role', value)}
                      forms={forms}
                    />
                  )}
                </Field>

                {isProvider && (
                  <div className={classes.providerFieldsContainer}>
                    <Typography className={classes.providerFieldsTitle}>
                      Provider Information Required
                    </Typography>
                    <FormikTextField
                      type="text"
                      name="providerTitle"
                      label={forms.title}
                      backendError={backendErrors.providerTitle}
                      required
                      placeholder="e.g., MD, DO, NP"
                    />
                    <FormikTextField
                      type="text"
                      name="providerNPINumber"
                      label={forms.npiNumber}
                      backendError={backendErrors.providerNPINumber}
                      required
                      placeholder="10-digit NPI number"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* User Permissions Section */}
            <UserPermissionSection
              values={values}
              backendErrors={backendErrors}
              setBackendErrors={setBackendErrors}
              isOwnerAccDS={isOwnerAccDS}
              classes={classes}
            />

            {/* Allowed Accounts Section */}
            {isOwnerAccDS && (
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  {forms.allowedAccounts || 'Account Access'}
                </Typography>
                <div className={classes.sectionContent}>
                  <AllowedAccountsSection
                    values={values}
                    backendErrors={backendErrors}
                    toggleAllowedAccountsModal={toggleAllowedAccountsModal}
                  />
                </div>
              </div>
            )}
          </Grid>
        </Form>
      ) : (
        <ContentWrapper>
          <Loader fullHeight />
        </ContentWrapper>
      )}
    </>
  );
};

UserForm.propTypes = {
  values: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    role: PropTypes.string,
    providerTitle: PropTypes.string,
    providerNPINumber: PropTypes.string,
    managesUsers: PropTypes.bool,
    administerClaims: PropTypes.bool,
    administerClaimReports: PropTypes.bool,
    receiveClaimsEmails: PropTypes.bool,
    doesUserExist: PropTypes.bool,
  }).isRequired,
  setBackendErrors: PropTypes.func.isRequired,
  backendErrors: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    role: PropTypes.string,
    providerTitle: PropTypes.string,
    providerNPINumber: PropTypes.string,
    'userPermissions.managesUsers': PropTypes.string,
    'userPermissions.administerClaims': PropTypes.string,
    'userPermissions.receiveClaimsEmails': PropTypes.string,
    'userPermissions.receiveSurveysEmails': PropTypes.string,
  }),
};

UserForm.defaultProps = {
  backendErrors: undefined,
};

export default UserForm;
